import { ItemList } from 'schema-dts'
import { JsonLd } from 'react-schemaorg'
import { useEffect, useRef, useState } from 'react'

import { useStoreState } from '@/store'

import SortBar from './SortBar'
import Icon from './elements/Icon'
import Filters from './filter/Filters'
import ShimmerRows from './results/ShimmerRows'
import MobileFilters from './filter/MobileFilters'
import SearchResults from './results/SearchResults'
import NoSearchResults from './results/NoSearchResults'

const Table = () => {
  const { isSearching, results, totalResults, contentModifiedAt } =
    useStoreState(state => state.search)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFilterBtnOutbound, setIsFilterBtnOutbound] = useState(false)
  const [showMobiFilter, setShowMobiFilter] = useState(false)

  useEffect(() => {
    // on mobile, if filter button scrolls out of view, show a sticky one at the bottom
    if (containerRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsFilterBtnOutbound(
            entry.intersectionRatio < 1 && entry.boundingClientRect.y < 0
          )
        },
        { rootMargin: '120px 0px 0px 0px', threshold: 1 }
      )
      observer.observe(containerRef.current)
      return () => observer.disconnect()
    }
  }, [])

  return (
    <>
      <JsonLd<ItemList>
        item={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: results.map((result, index) => ({
            '@type': 'ListItem',
            position: (index + 1).toString(),
            item: {
              '@type': 'RealEstateAgent',
              name: result.full_name,
              description: result.details.description,
              image: {
                '@type': 'ImageObject',
                contentUrl: result.details.avatar_url
              }
            }
          }))
        }}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-3 hidden lg:block pr-4 box-shadow-right">
          <Filters />
        </div>
        <div className="lg:col-span-9 lg:px-4">
          <div ref={containerRef}>
            <SortBar
              updatedOn={contentModifiedAt}
              total={totalResults}
              onFilterClick={() => setShowMobiFilter(true)}
            />
          </div>

          {isSearching >= 2 ? (
            <ShimmerRows className="my-6" />
          ) : results.length ? (
            <SearchResults results={results} total={totalResults} />
          ) : (
            <NoSearchResults />
          )}
        </div>
      </div>

      <div
        className={`bg-white p-4 -mx-4 mb-12 text-center box-shadow-top lg:hidden ${
          isFilterBtnOutbound ? 'sticky' : 'hidden'
        }`}
        style={{ bottom: 0 }}
      >
        <button
          className="Button Button--secondary text-sm w-full"
          data-testid="mobile-filter-button-bottom"
          onClick={() => setShowMobiFilter(true)}
        >
          <Icon variant="filter" className="Icon--xs mr-2" />
          Filters
        </button>
      </div>

      <MobileFilters
        visible={showMobiFilter}
        onClose={() => setShowMobiFilter(false)}
        className="lg:hidden"
      />
    </>
  )
}

export default Table
